import { put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  getAllActivitiesPerOutletSuccess,
  fetchOutletStatusSuccess,
  fetchActivityErrorSuccess,
  getOutletDeliveryStatusSuccess,
  updateOutletDeliveryStatusSuccess,
  getHelpLinkSuccess,
  storeBranchData,
  branchDataCallComplete,
} from "../../slices/OutletStatusSlice";
import { getCustomerDisplayCampaign } from "../../slices/CustomerDisplay";
import { setKioskConfigurationId } from "../../slices/kioskConfigSlice";

/**
 * Uplifted from `redux-store/saga/outletStatus` (apps/web + apps/mobile).
 *
 * A SPLIT. SEVEN of the eleven workers are identical and live here; three diverge and one is
 * web-only, so those — and `root`, which differs in both content and length — sit in the
 * platform files.
 *
 * The divergences, so they are not re-derived:
 *   1. `getAllOutletsFun` — web has a `finally { put(allOutletsCallComplete()) }`; mobile has no
 *      finally and never dispatches it (mobile's slice does not import the action either).
 *   2. `toggleOutletFun` — web calls `ToastNotifySuccess("Status Updated Successfully")`; mobile
 *      has that line COMMENTED OUT in the app source. Mobile deliberately shows no toast, so the
 *      core native file must not either.
 *   3. `bulkOutletToggleFun` — web guards `if (!response?.code)`, mobile `if (!response)`. A REAL
 *      logic difference: an error object with a `code` is falsy-checked differently.
 *   4. web-only worker `toggleBranchFunc` (and its web-only `BRANCH_OPTIONS`).
 *
 * `getBranchDataFunc` USED TO diverge and now lives here: the two bodies were identical except
 * that web dispatched `getCustomerDisplayCampaign` unconditionally while mobile guarded on
 * `response?.customerDisplayId`. The guard is kept — without it web requests
 * `/customerdisplay/undefined/fetch` whenever a branch has no customer display — so web now
 * skips that call in exactly the case where it could never have succeeded.
 *
 * ⚠️ Bare folder imports are REQUIRED in a `shared.ts` (hazard 16).
 *
 * Dropped under standing rule 2(b): web imported `ToastNotifyError` and never used it (0 uses).
 * Mobile's commented-out import went with the move.
 */
export function* fetchActivities(action: any): Generator<any, void, any> {
  console.log("action123", action);
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.fetchActivities(),
      data: {
        group: "ORDER_AGGREGATORS",
        referenceId: action?.payload,
        pageSize: 20,
        lastKey: action?.payload?.lastKey,
      },
    });
    yield put(getAllActivitiesPerOutletSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* fetchOutletStatusFun(action: any): Generator<any, void, any> {
  try {
    let response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.fetchOutletStatus(),
      data: action?.payload,
    });
    if (!response?.code && Object.keys(response)?.length) {
      response.type = action?.payload?.type;
    }
    response.lastCheckedDate = new Date();
    yield put(fetchOutletStatusSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* fetchActivityErrorFun(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.OUTLETSTATUS.fetchActivityError(action?.payload),
    });
    yield put(fetchActivityErrorSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* getOutletDeliveryStatusFun(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.getOutletDeliveryStatus(),
      data: action?.payload,
    });
    yield put(getOutletDeliveryStatusSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* updateOutletDeliveryStatusFun(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.updateOutletDeliveryStatus(),
      data: action?.payload,
    });
    yield put(updateOutletDeliveryStatusSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* fetchHelpLink(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.OUTLETSTATUS.fetchHelpLink(),
      data: action?.payload,
    });
    yield put(getHelpLinkSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

export function* getBranchDataFunc(action: any): Generator<any, void, any> {
  const branchId = action.payload;
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.OUTLETSTATUS.getBranchData(branchId),
    });
    if (!response?.code) {
      yield put(storeBranchData(response));
      if (response?.customerDisplayId) {
        yield put(
          getCustomerDisplayCampaign({
            customerDisplayId: response.customerDisplayId,
          }),
        );
      }
      // The branch names its kiosk configuration — or omits the field when it has none, so an
      // absent id is stored as `null` (which also drops a config left from an earlier mapping;
      // see `setKioskConfigurationId`). The config itself is fetched where the kiosk mounts
      // (`useKioskConfigFetch`, from `KioskApp`), never here: a POS terminal must not call a
      // `Kiosk`-module route at login.
      yield put(setKioskConfigurationId(response?.kioskConfigurationId || null));
    }
  } catch (error) {
    console.log("error", error);
  } finally {
    yield put(branchDataCallComplete());
  }
}
